import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, FileText, AlertTriangle, 
  ArrowUpDown, Search, Loader2, ChevronDown, ChevronUp,
  Award, PieChart, Activity, Target
} from 'lucide-react';

const InformesPage = () => {
  const [activeTab, setActiveTab] = useState('resumen');
  const [searchCups, setSearchCups] = useState('');
  const [selectedCups, setSelectedCups] = useState(null);

  // Tabs de informes
  const tabs = [
    { id: 'resumen', label: 'Resumen General', icon: Activity },
    { id: 'cups', label: 'Evolución CUPS', icon: TrendingUp },
    { id: 'auditoria', label: 'Auditoría CUPS', icon: AlertTriangle },
    { id: 'contratos', label: 'Contratos de Canal', icon: FileText },
    { id: 'tarifas', label: 'Análisis Tarifas', icon: BarChart3 },
    { id: 'agentes', label: 'Análisis de Canales', icon: Users },
  ];

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <PieChart className="w-8 h-8 text-blue-600" />
          Módulo de Informes
        </h1>
        <p className="text-gray-600 mt-2">
          Análisis y reportes de comisiones, decomisiones y auditorías
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 border-b-2 font-medium transition-colors whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'resumen' && <ResumenGeneral />}
          {activeTab === 'cups' && (
            <EvolucionCups 
              searchCups={searchCups}
              setSearchCups={setSearchCups}
              selectedCups={selectedCups}
              setSelectedCups={setSelectedCups}
            />
          )}
          {activeTab === 'auditoria' && <AuditoriaCups />}
          {activeTab === 'contratos' && <ContratosAgencia />}
          {activeTab === 'tarifas' && <AnalisisTarifas />}
          {activeTab === 'agentes' && <AnalisisAgentes />}
        </div>
      </div>
    </div>
  );
};

// ==================== RESUMEN GENERAL ====================
const ResumenGeneral = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['resumen-general'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/resumen-general/');
      return res.data;
    }
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-ES').format(value || 0);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Dashboard General</h2>
      
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total CUPS"
          value={formatNumber(data?.total_cups)}
          icon={Target}
          color="blue"
        />
        <MetricCard
          title="Total Liquidación"
          value={formatCurrency(data?.total_liquidacion)}
          icon={TrendingUp}
          color="green"
        />
        <MetricCard
          title="Total Decomisión"
          value={formatCurrency(data?.total_decomision)}
          icon={TrendingUp}
          color="red"
        />
        <MetricCard
          title="Neto"
          value={formatCurrency(data?.neto_total)}
          icon={Award}
          color="purple"
        />
      </div>

      {/* Estados de liquidación */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado de Liquidaciones</h3>
          <div className="space-y-3">
            <StatusBar
              label="Calculadas"
              value={data?.liquidaciones_calculadas || 0}
              total={data?.total_lineas || 1}
              color="green"
            />
            <StatusBar
              label="Pendientes"
              value={data?.liquidaciones_pendientes || 0}
              total={data?.total_lineas || 1}
              color="yellow"
            />
            <StatusBar
              label="Errores"
              value={data?.liquidaciones_error || 0}
              total={data?.total_lineas || 1}
              color="red"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado de Decomisiones</h3>
          <div className="space-y-3">
            <StatusBar
              label="Calculadas"
              value={data?.decomisiones_calculadas || 0}
              total={data?.total_lineas || 1}
              color="green"
            />
            <StatusBar
              label="Pendientes"
              value={data?.decomisiones_pendientes || 0}
              total={data?.total_lineas || 1}
              color="yellow"
            />
            <StatusBar
              label="Errores"
              value={data?.decomisiones_error || 0}
              total={data?.total_lineas || 1}
              color="red"
            />
          </div>
        </div>
      </div>

      {/* Resumen de entidades */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Canales Activos</p>
              <p className="text-3xl font-bold text-blue-900">{formatNumber(data?.total_canales)}</p>
            </div>
            <Users className="w-12 h-12 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Tarifas Diferentes</p>
              <p className="text-3xl font-bold text-purple-900">{formatNumber(data?.total_tarifas)}</p>
            </div>
            <FileText className="w-12 h-12 text-purple-400" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Líneas Procesadas</p>
              <p className="text-3xl font-bold text-green-900">{formatNumber(data?.total_lineas)}</p>
            </div>
            <BarChart3 className="w-12 h-12 text-green-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== EVOLUCIÓN CUPS ====================
const EvolucionCups = ({ searchCups, setSearchCups, selectedCups, setSelectedCups }) => {
  const [inputCups, setInputCups] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['evolucion-cups', selectedCups],
    queryFn: async () => {
      if (!selectedCups) return null;
      const res = await axios.get(`/api/informes/evolucion-cups/${selectedCups}/`);
      return res.data;
    },
    enabled: !!selectedCups
  });

  const handleSearch = () => {
    if (inputCups.trim()) {
      setSelectedCups(inputCups.trim());
    } else {
      toast.error('Ingrese un CUPS para buscar');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Evolución de CUPS</h2>
        <p className="text-gray-600 mb-4">
          Consulta la historia completa de un CUPS: todas sus líneas de historificación, 
          comisiones, decomisiones y cambios de canal.
        </p>
      </div>

      {/* Buscador */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={inputCups}
            onChange={(e) => setInputCups(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Ingrese el CUPS a consultar..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Buscar
        </button>
      </div>

      {/* Resultados */}
      {isLoading && <LoadingSpinner />}
      
      {data && !data.error && (
        <div className="space-y-6">
          {/* Información del cliente */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Información del CUPS</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-blue-600 font-medium">CUPS</p>
                <p className="text-lg font-mono text-blue-900">{data.cups}</p>
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Cliente</p>
                <p className="text-lg text-blue-900">{data.cliente?.nombre_completo || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Identificador</p>
                <p className="text-lg text-blue-900">{data.cliente?.identificador || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Resumen financiero */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              title="Total Líneas"
              value={data.resumen?.total_lineas}
              icon={FileText}
              color="gray"
              small
            />
            <MetricCard
              title="Total Liquidación"
              value={formatCurrency(data.resumen?.total_liquidacion)}
              icon={TrendingUp}
              color="green"
              small
            />
            <MetricCard
              title="Total Decomisión"
              value={formatCurrency(data.resumen?.total_decomision)}
              icon={TrendingUp}
              color="red"
              small
            />
            <MetricCard
              title="Neto"
              value={formatCurrency(data.resumen?.neto)}
              icon={Award}
              color="purple"
              small
            />
            <MetricCard
              title="Cambios de Canal"
              value={data.resumen?.numero_cambios_canal}
              icon={ArrowUpDown}
              color="orange"
              small
            />
          </div>

          {/* Cambios de canal */}
          {data.cambios_canal && data.cambios_canal.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Cambios de Canal Detectados
              </h3>
              <div className="space-y-2">
                {data.cambios_canal.map((cambio, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <span className="text-orange-600 font-medium">{formatDate(cambio.fecha)}</span>
                    <span className="text-gray-600">De:</span>
                    <span className="font-semibold text-gray-900">{cambio.canal_anterior}</span>
                    <span className="text-gray-600">→ A:</span>
                    <span className="font-semibold text-gray-900">{cambio.canal_nuevo}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabla de historificación */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Historificación Completa</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Inicio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Fin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esquema</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Liquidación</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Decomisión</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado Hist.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.historificacion?.map((linea, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDate(linea.fecha_inicio_producto)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDate(linea.fecha_fin_producto)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{linea.dias_incluidos_liquidacion || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{linea.canal}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">{linea.id_tarifa_precios}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                          {linea.esquema_liquidacion || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <EstadoBadge estado={linea.liquidacion_estado} />
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                        {linea.liquidacion ? formatCurrency(linea.liquidacion) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-red-700">
                        {linea.decomision ? formatCurrency(linea.decomision) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <EstadoHistorificacionBadge estado={linea.estado_historificacion} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {data && data.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">CUPS no encontrado</h3>
          <p className="text-red-600">
            No se encontraron registros para el CUPS: <span className="font-mono font-bold">{data.cups}</span>
          </p>
        </div>
      )}
    </div>
  );
};

// ==================== AUDITORÍA CUPS ====================
const AuditoriaCups = () => {
  const [selectedView, setSelectedView] = useState('pendientes');

  const { data, isLoading } = useQuery({
    queryKey: ['auditoria-cups'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/auditoria-cups/');
      return res.data;
    }
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Auditoría de CUPS</h2>
        <p className="text-gray-600">
          Revisa CUPS con liquidaciones pendientes, errores y cambios de canal
        </p>
      </div>

      {/* Selector de vista */}
      <div className="flex gap-3">
        <button
          onClick={() => setSelectedView('pendientes')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'pendientes'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          CUPS Pendientes ({data?.total_cups_sin_comisionar || 0})
        </button>
        <button
          onClick={() => setSelectedView('cambios')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'cambios'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Cambios de Canal ({data?.total_cups_cambio_canal || 0})
        </button>
      </div>

      {/* Vista de CUPS pendientes */}
      {selectedView === 'pendientes' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">CUPS con Pendientes o Errores</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CUPS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Líneas</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pend. Liq.</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pend. Decom.</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Errores Liq.</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Errores Decom.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.cups_sin_comisionar?.map((cups, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{cups.cups}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{cups.nombre_completo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cups.canal}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600">{cups.total_lineas}</td>
                    <td className="px-4 py-3 text-center">
                      {cups.pendientes_liquidacion > 0 ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {cups.pendientes_liquidacion}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {cups.pendientes_decomision > 0 ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {cups.pendientes_decomision}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {cups.errores_liquidacion > 0 ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {cups.errores_liquidacion}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {cups.errores_decomision > 0 ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {cups.errores_decomision}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vista de cambios de canal */}
      {selectedView === 'cambios' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">CUPS con Cambios de Canal</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CUPS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal Anterior</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal Nuevo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Cambio</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Canales</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.cups_cambio_canal?.map((cups, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{cups.cups}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{cups.nombre_completo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cups.canal_anterior}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{cups.canal_nuevo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(cups.fecha_cambio).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {cups.total_canales}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== CONTRATOS AGENCIA ====================
const ContratosAgencia = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['contratos-agencia'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/contratos-agencia/');
      return res.data;
    }
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Contratos de Canal</h2>
        <p className="text-gray-600">
          Comparación de condiciones especiales de canales vs tarifas base
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Total de Contratos: {data?.total_contratos || 0}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esquema</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Activo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% Broker</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Com. €/MWh</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Com. €/CUPS</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Diferencias</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Veces Usado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.contratos?.map((contrato, idx) => {
                const tieneDiferencias = contrato.diff_porcentaje || contrato.diff_emwh || contrato.diff_ecups;
                
                return (
                  <tr key={idx} className={`hover:bg-gray-50 ${tieneDiferencias ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{contrato.nombre_agencia}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600 text-xs">{contrato.tarifa_precio_neuro}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                        {contrato.esquema_liquidacion}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {contrato.activo ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Sí
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="space-y-1">
                        <div className={contrato.diff_porcentaje ? 'font-bold text-blue-700' : 'text-gray-900'}>
                          {contrato.contrato_porcentaje}%
                        </div>
                        <div className="text-xs text-gray-500">
                          (Tarifa: {contrato.tarifa_porcentaje}%)
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="space-y-1">
                        <div className={contrato.diff_emwh ? 'font-bold text-blue-700' : 'text-gray-900'}>
                          {contrato.contrato_comision_emwh}
                        </div>
                        <div className="text-xs text-gray-500">
                          (Tarifa: {contrato.tarifa_comision_emwh})
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="space-y-1">
                        <div className={contrato.diff_ecups ? 'font-bold text-blue-700' : 'text-gray-900'}>
                          {contrato.contrato_comision_ecups}
                        </div>
                        <div className="text-xs text-gray-500">
                          (Tarifa: {contrato.tarifa_comision_ecups})
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {tieneDiferencias ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          Sí
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                      {contrato.veces_usado}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== ANÁLISIS TARIFAS ====================
const AnalisisTarifas = () => {
  const [selectedView, setSelectedView] = useState('dispersion');

  const { data: dispersion, isLoading: loadingDispersion } = useQuery({
    queryKey: ['tarifas-dispersion'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/tarifas/dispersion/');
      return res.data;
    },
    enabled: selectedView === 'dispersion'
  });

  const { data: ranking, isLoading: loadingRanking } = useQuery({
    queryKey: ['tarifas-ranking'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/tarifas/ranking-comisiones/');
      return res.data;
    },
    enabled: selectedView === 'ranking'
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Análisis de Tarifas</h2>
        <p className="text-gray-600">
          Estadísticas de uso y rendimiento de tarifas
        </p>
      </div>

      {/* Selector de vista */}
      <div className="flex gap-3">
        <button
          onClick={() => setSelectedView('dispersion')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'dispersion'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Dispersión de Contratos
        </button>
        <button
          onClick={() => setSelectedView('ranking')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'ranking'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Ranking de Comisiones
        </button>
      </div>

      {/* Vista de dispersión */}
      {selectedView === 'dispersion' && (
        <>
          {loadingDispersion ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Dispersión por Tarifa ({dispersion?.total_tarifas || 0} tarifas)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esquema</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUPS</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Líneas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Canales</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Liquidadas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Decomisionadas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dispersion?.tarifas?.map((tarifa, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 text-xs">{tarifa.id_tarifa_precios}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                            {tarifa.esquema_liquidacion || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">{tarifa.total_cups}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{tarifa.total_lineas}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{tarifa.total_canales}</td>
                        <td className="px-4 py-3 text-sm text-center text-green-700">{tarifa.liquidadas}</td>
                        <td className="px-4 py-3 text-sm text-center text-red-700">{tarifa.decomisionadas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vista de ranking */}
      {selectedView === 'ranking' && (
        <>
          {loadingRanking ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ranking de Comisiones ({ranking?.total_tarifas || 0} tarifas)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Posición</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esquema</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUPS</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Liq.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Decom.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Neto</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Promedio Liq.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ranking?.ranking?.map((tarifa, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-center">
                          {idx < 3 ? (
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-800 font-bold">
                              {idx + 1}
                            </span>
                          ) : (
                            <span className="text-gray-600">{idx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 text-xs">{tarifa.id_tarifa_precios}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                            {tarifa.esquema_liquidacion || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{tarifa.total_cups}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                          {formatCurrency(tarifa.total_liquidacion)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-red-700">
                          {formatCurrency(tarifa.total_decomision)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-purple-700">
                          {formatCurrency(tarifa.neto)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                          {formatCurrency(tarifa.promedio_liquidacion)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== ANÁLISIS AGENTES ====================
const AnalisisAgentes = () => {
  const [selectedView, setSelectedView] = useState('ranking');

  const { data: ranking, isLoading: loadingRanking } = useQuery({
    queryKey: ['agentes-ranking'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/agentes/ranking/');
      return res.data;
    },
    enabled: selectedView === 'ranking'
  });

  const { data: dispersion, isLoading: loadingDispersion } = useQuery({
    queryKey: ['agentes-dispersion'],
    queryFn: async () => {
      const res = await axios.get('/api/informes/agentes/dispersion-tarifas/');
      return res.data;
    },
    enabled: selectedView === 'dispersion'
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Análisis de Canales</h2>
        <p className="text-gray-600">
          Rendimiento y distribución de tarifas por canal
        </p>
      </div>

      {/* Selector de vista */}
      <div className="flex gap-3">
        <button
          onClick={() => setSelectedView('ranking')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'ranking'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Ranking de Comisiones
        </button>
        <button
          onClick={() => setSelectedView('dispersion')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedView === 'dispersion'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Dispersión de Tarifas
        </button>
      </div>

      {/* Vista de ranking */}
      {selectedView === 'ranking' && (
        <>
          {loadingRanking ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ranking de Canales ({ranking?.total_agentes || 0} canales)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Posición</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUPS</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tarifas Distintas</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Liq.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Decom.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Neto</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Promedio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ranking?.ranking?.map((agente, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-center">
                          {idx < 3 ? (
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-800 font-bold">
                              {idx + 1}
                            </span>
                          ) : (
                            <span className="text-gray-600">{idx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{agente.canal}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{agente.total_cups}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {agente.tarifas_diferentes}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                          {formatCurrency(agente.total_liquidacion)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-red-700">
                          {formatCurrency(agente.total_decomision)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-purple-700">
                          {formatCurrency(agente.neto)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                          {formatCurrency(agente.promedio_liquidacion)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vista de dispersión */}
      {selectedView === 'dispersion' && (
        <>
          {loadingDispersion ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Dispersión de Tarifas por Canal
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esquema</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUPS</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Líneas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">% del Canal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Liq.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Decom.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dispersion?.dispersiones?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.canal}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600 text-xs">{item.id_tarifa_precios}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                            {item.esquema_liquidacion || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{item.total_cups}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{item.total_lineas}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {item.porcentaje_lineas_canal}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                          {formatCurrency(item.total_liquidacion)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-red-700">
                          {formatCurrency(item.total_decomision)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== COMPONENTES AUXILIARES ====================
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
  </div>
);

const MetricCard = ({ title, value, icon: Icon, color = 'blue', small = false }) => {
  const colorClasses = {
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-600 text-blue-900',
    green: 'from-green-50 to-green-100 border-green-200 text-green-600 text-green-900',
    red: 'from-red-50 to-red-100 border-red-200 text-red-600 text-red-900',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-600 text-purple-900',
    orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-600 text-orange-900',
    gray: 'from-gray-50 to-gray-100 border-gray-200 text-gray-600 text-gray-900',
  };

  const [fromColor, toColor, borderColor, iconColor, textColor] = colorClasses[color].split(' ');

  return (
    <div className={`bg-gradient-to-br ${fromColor} ${toColor} border ${borderColor} rounded-lg ${small ? 'p-4' : 'p-6'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${iconColor}`}>{title}</p>
          <p className={`${small ? 'text-2xl' : 'text-3xl'} font-bold ${textColor}`}>{value}</p>
        </div>
        <Icon className={`${small ? 'w-8 h-8' : 'w-12 h-12'} ${iconColor.replace('text-', 'text-').replace('-600', '-400')}`} />
      </div>
    </div>
  );
};

const StatusBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-semibold text-gray-900">
          {new Intl.NumberFormat('es-ES').format(value)} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const EstadoBadge = ({ estado }) => {
  const colorClasses = {
    calculada: 'bg-green-100 text-green-800',
    pendiente: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    no_liquidable: 'bg-rose-100 text-rose-800',
    bloqueada: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colorClasses[estado] || 'bg-gray-100 text-gray-600'}`}>
      {estado || 'N/A'}
    </span>
  );
};

const EstadoHistorificacionBadge = ({ estado }) => {
  const colorClasses = {
    'PENDIENTE LIQUIDAR': 'bg-yellow-100 text-yellow-800',
    'NO LIQUIDABLE': 'bg-rose-100 text-rose-800',
    'ACTIVACION FUTURA': 'bg-indigo-100 text-indigo-800',
    'LIQUIDADO': 'bg-blue-100 text-blue-800',
    'PENDIENTE DECOMISIONAR': 'bg-orange-100 text-orange-800',
    'FINALIZADO': 'bg-green-100 text-green-800',
    'ERROR LIQUIDACIÓN': 'bg-red-100 text-red-800',
    'ERROR DECOMISIÓN': 'bg-red-100 text-red-800',
    'LIQ. BLOQUEADA': 'bg-purple-100 text-purple-800',
    'DECOM. BLOQUEADA': 'bg-purple-100 text-purple-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colorClasses[estado] || 'bg-gray-100 text-gray-600'}`}>
      {estado || 'N/A'}
    </span>
  );
};

export default InformesPage;
